import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { PdpRespDetail } from './PdpRespDetail';
import { PdpSharedDetail } from '../PdpSharedDetails/PdpSharedDetail';
import { PdpSharedDetailCondition } from '../PdpSharedDetails/PdpSharedDetailCondition';
import { detailsTitle } from '../messages/pdpDetailsMessages';

// helpers
import { slugify } from '../../utils/slugify';
import get from 'lodash.get';

// styles
import styles from './PdpRespDetails.scss';

const NUMBER_OF_COLUMNS = 4;
const detailsToHideInPrint = ['PRODUCTION_TIME'];

function PdpRespDetailsComponent(props) {
    const { item, currency, relay, selectedSkuId } = props;
    const details = item.pdpDetailsDisplay;
    const numberOfRows = Math.ceil(details.length / NUMBER_OF_COLUMNS);
    const { vertical } = item;

    return (
        <div className={styles.wrapper} data-tn="pdp-details">
            <h2 className={styles.header}>{detailsTitle}</h2>
            <div className="rowFlex">
                {details.reduce(
                    (detailComponents, detail, index) => {
                        const { detailKey, detailDisplayName, detailValues, delimiter } = detail;
                        const columnComponents = detailComponents[detailComponents.length - 1];
                        // exclude this from showing in print layout
                        const shouldHideInPrint = detailsToHideInPrint.includes(detailKey);
                        // Add detail to column
                        columnComponents.push(
                            <PdpRespDetail
                                delimiter={delimiter}
                                title={detailDisplayName}
                                dataTn={slugify(detailDisplayName)}
                                key={`${detailKey}-${index}`}
                                printHide={shouldHideInPrint}
                            >
                                {// VERTICALS-1 Do not display condition link for art vertical
                                // Will be added after initial 228 release
                                detailKey === 'CONDITION' && vertical !== 'art' ? (
                                    <PdpSharedDetailCondition
                                        detailValues={detailValues}
                                        currency={currency}
                                        delimiter={delimiter}
                                        relay={relay}
                                        itemId={get(item, 'serviceId')}
                                    />
                                ) : (
                                    <PdpSharedDetail
                                        detailValues={detailValues}
                                        currency={currency}
                                        delimiter={delimiter}
                                        selectedSkuId={selectedSkuId}
                                    />
                                )}
                            </PdpRespDetail>
                        );
                        // check if column is full
                        if (
                            columnComponents.length === numberOfRows ||
                            index === details.length - 1
                        ) {
                            // render columnComponents in column
                            detailComponents[detailComponents.length - 1] = (
                                <ul
                                    key={`column-${detailKey}-${index}`}
                                    className={`colXl3 colXs6 ${styles.column}`}
                                >
                                    {columnComponents}
                                </ul>
                            );
                            // if not the last detail, then initialize next column of details
                            if (index !== details.length - 1) {
                                detailComponents[detailComponents.length] = [];
                            }
                        }
                        return detailComponents;
                    },
                    [[]]
                )}
            </div>
        </div>
    );
}

PdpRespDetailsComponent.propTypes = {
    currency: PropTypes.string,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
};

export const PdpRespDetails = createFragmentContainer(PdpRespDetailsComponent, {
    item: graphql`
        fragment PdpRespDetails_item on Item {
            serviceId
            vertical
            pdpDetailsDisplay {
                detailKey
                delimiter
                detailDisplayName
                detailValues {
                    ...PdpSharedDetail_detailValues
                }
            }
        }
    `,
});
